// inv/src/seo/seoHead.jsx
import { useEffect } from 'react';

const DEFAULT_OG_IMAGE =
  'https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/seo-data/homepage/seo-homepage-en.png';

export default function SeoHead({
  lang = 'en',
  title,
  description,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  prevUrl,
  nextUrl,
  alternates = [],
  jsonLd = []
}) {
  if (typeof window === 'undefined') {
    const finalOgImage = ogImage || DEFAULT_OG_IMAGE;
    global.__SEO_DATA__ = {
      lang,
      title,
      description,
      canonicalUrl,
      ogTitle: ogTitle || title,
      ogDescription: ogDescription || description,
      ogImage: finalOgImage,
      ogUrl,
      ogType,
      twitterCard,
      prevUrl,
      nextUrl,
      alternates,
      jsonLd
    };
  }

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (lang) {
      document.documentElement.lang = lang;
    }

    if (title) {
      document.title = title;
    } else {
      document.title = 'crino.io - Find and analyze investors online';
    }

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        const tagName = selector.replace(/\[.*\]/, '').split('=')[0];
        el = document.createElement(tagName);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (description)
      setMeta('meta[name="description"]', 'content', description);

    if (canonicalUrl) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    const ogTags = {
      'og:type': ogType,
      'og:url': ogUrl,
      'og:title': ogTitle || title,
      'og:description': ogDescription || description,
      'og:image': ogImage || DEFAULT_OG_IMAGE,
      'og:image:width': '1200',
      'og:image:height': '630',
    };
    Object.entries(ogTags).forEach(([prop, content]) => {
      if (!content) return;
      setMeta(`meta[property="${prop}"]`, 'content', content);
    });

    const twitterTags = {
      'twitter:card': twitterCard,
      'twitter:title': ogTitle || title,
      'twitter:description': ogDescription || description,
      'twitter:image': ogImage || DEFAULT_OG_IMAGE,
    };
    Object.entries(twitterTags).forEach(([name, content]) => {
      if (!content) return;
      setMeta(`meta[name="${name}"]`, 'content', content);
    });

    const setLink = (rel, href) => {
      if (!href) return;
      let link = document.head.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    setLink('prev', prevUrl);
    setLink('next', nextUrl);

    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => {
      link.remove();
    });

    alternates.forEach((alternate) => {
      if (!alternate?.lang || !alternate?.href) return;
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', alternate.lang);
      link.setAttribute('href', alternate.href);
      document.head.appendChild(link);
    });

    document.head.querySelectorAll('script[type="application/ld+json"][data-seo-json-ld]').forEach((script) => {
      script.remove();
    });

    const jsonLdItems = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : [jsonLd].filter(Boolean);
    jsonLdItems.forEach((item, index) => {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-json-ld', String(index));
      script.textContent = JSON.stringify(item)
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026');
      document.head.appendChild(script);
    });

  }, [
    title,
    description,
    canonicalUrl,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    twitterCard,
    prevUrl,
    nextUrl,
    alternates,
    jsonLd
  ]);

  return null;
}
